import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import { updateSession } from '@repancy/core';
import { testIds } from '../../lib/testIds';

interface UserPromptEditorProps {
  sessionId: string;
  onRun: () => void;
}

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  fontWeight: 'bold',
  marginBottom: 4,
  display: 'block',
  color: '#000',
};

const textareaStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 180,
  resize: 'vertical',
  fontFamily: "var(--xp-font-mono)",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.5,
  padding: 6,
  background: '#fff',
  color: '#000',
  borderStyle: 'solid',
  borderWidth: 2,
  outline: 'none',
  boxSizing: 'border-box' as const,
};

const btnStyle: React.CSSProperties = {
  fontFamily: "var(--xp-font-body)",
  fontSize: 12,
  padding: '4px 16px',
  background: '#ece9d8',
  color: '#000',
  borderStyle: 'solid',
  cursor: 'pointer',
  marginTop: 6,
};

export function UserPromptEditor({ sessionId, onRun }: UserPromptEditorProps) {
  const dispatch = useAppDispatch();
  const userPrompt = useAppSelector(
    (s) => s.promptSessions.sessions.entities[sessionId]?.userPrompt ?? '',
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(updateSession({ id: sessionId, changes: { userPrompt: e.target.value } }));
    },
    [dispatch, sessionId],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onRun();
      }
    },
    [onRun],
  );

  return (
    <div style={{ marginBottom: 8 }}>
      <label style={labelStyle}>User Prompt</label>
      <textarea
        style={textareaStyle}
        value={userPrompt}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Review Section 4.2 of the attached purchase agreement and identify any indemnification caps that fall below market standard for a transaction of this size."
        data-testid={testIds.promptLab.userPrompt}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button
          style={btnStyle}
          onClick={onRun}
          data-testid={testIds.promptLab.runButton}
        >
          Send (Ctrl+Enter)
        </button>
      </div>
    </div>
  );
}
